import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

/**
 * SSO auth for the management SPA. There's no session cookie here (this
 * Worker holds no server-side state at all — see worker/wrangler.toml's
 * header) — just a short-lived (15 min) bearer JWT minted by app.pure.dev's
 * /api/auth/sso/issue?aud=management and handed back in the URL fragment.
 *
 * sessionStorage (not localStorage) caches it across a page reload within
 * its own TTL, and — the actual point of this file existing now that the
 * dashboard is a real SPA instead of separate static HTML pages — across
 * client-side route changes, which never used to redo the SSO round trip
 * anyway (this is one <script> tag's module state) but DID before this
 * SPA existed, when Box Approvals and Dependencies were two entirely
 * separate page loads.
 *
 * ADDED 2026-08-27 — proactive silent refresh: this used to fetch a token
 * ONCE at load and never touch it again, so any tab left open (or just
 * idle) past its 15-minute TTL failed every subsequent API call with
 * jose's raw `"exp" claim timestamp check failed"`, recoverable only by a
 * manual reload. app.pure.dev's own /api/auth/sso/refresh endpoint
 * (CORS-enabled, credentialed, built specifically "for the frontend to
 * refresh in-place without a navigation flicker" — see internal/app's
 * sso.ts) already existed for exactly this and was simply never called
 * from here. A timer now calls it a couple minutes before the cached
 * token's exp, updates context + sessionStorage, and reschedules itself
 * off the NEW exp — so a tab can sit open indefinitely without ever
 * hitting an expired token, no user action needed. Falls back to the
 * full-page redirectToSso() only if refresh itself fails (e.g. the
 * app.pure.dev session cookie itself is gone — genuinely signed out).
 */

const APP_ORIGIN = "https://app.pure.dev";
const STORAGE_KEY = "pure_management_sso";

interface StoredToken {
  token: string;
  exp: number; // unix seconds
}

function readTokenFromHash(): StoredToken | null {
  const m = /token=([^&]+)&exp=(\d+)/.exec(location.hash.slice(1));
  if (!m) return null;
  history.replaceState(null, "", location.pathname); // scrub the fragment from the URL bar/history
  return { token: decodeURIComponent(m[1]!), exp: Number(m[2]) };
}

function readTokenFromStorage(): StoredToken | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredToken;
    if (!parsed.token || typeof parsed.exp !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
}

function storeToken(t: StoredToken): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(t));
  } catch {
    // sessionStorage unavailable (private browsing etc.) — token still
    // works for this page load via in-memory state, just won't survive
    // a reload. Not worth failing sign-in over.
  }
}

// A little slack before the real expiry so a request doesn't start with a
// token that expires mid-flight.
const EXPIRY_SLACK_SECONDS = 15;
function isLive(t: StoredToken): boolean {
  return t.exp - EXPIRY_SLACK_SECONDS > Date.now() / 1000;
}

function redirectToSso(): void {
  const returnTo = location.origin + location.pathname;
  location.href =
    APP_ORIGIN +
    "/api/auth/sso/issue?aud=management&return_to=" +
    encodeURIComponent(returnTo);
}

interface RefreshResponse {
  ok: boolean;
  token?: string;
  exp?: number;
  reason?: string;
}

/** Silent renewal via app.pure.dev's dedicated CORS+credentialed refresh
 *  endpoint — `credentials: "include"` is required for the browser to
 *  send the app-session cookie cross-origin; without it this 401s with
 *  `not_signed_in` even for a genuinely signed-in user. Returns null on
 *  any failure (network error, expired app session, disallowed origin —
 *  the caller falls back to a full redirect either way, so the exact
 *  reason only matters for debugging, not control flow). */
async function refreshToken(): Promise<StoredToken | null> {
  try {
    const res = await fetch(
      APP_ORIGIN + "/api/auth/sso/refresh?aud=management",
      { credentials: "include" },
    );
    const body = (await res.json()) as RefreshResponse;
    if (!res.ok || !body.ok || !body.token || typeof body.exp !== "number") {
      return null;
    }
    return { token: body.token, exp: body.exp };
  } catch {
    return null; // network hiccup — caller retries on its own schedule
  }
}

interface AuthContextValue {
  token: string;
}
const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuthToken(): string {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuthToken used outside AuthGate");
  return ctx.token;
}

// How long before a token's exp to proactively refresh it. Well ahead of
// isLive()'s 15s slack (that's the "is this still even usable right now"
// cutoff for a cold-loaded token, a different concern) — this is "renew
// with plenty of runway left" so a slow/retried refresh, or a background
// tab's throttled timers, still lands comfortably before expiry.
const REFRESH_LEAD_SECONDS = 120;

/**
 * Resolves a live token before rendering `children` at all — redirects to
 * SSO and renders nothing further if none is available. Every page in this
 * app renders under this, so no page needs its own sign-in handling.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const [stored, setStored] = useState<StoredToken | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    const fromHash = readTokenFromHash();
    if (fromHash && isLive(fromHash)) {
      storeToken(fromHash);
      setStored(fromHash);
      return;
    }
    const fromStorage = readTokenFromStorage();
    if (fromStorage && isLive(fromStorage)) {
      setStored(fromStorage);
      return;
    }
    setRedirecting(true);
    redirectToSso();
  }, []);

  // Proactive silent refresh — see file header. Scheduled off the current
  // token's exp; a successful refresh updates `stored`, which re-runs this
  // effect (cleanup + re-schedule) off the NEW exp, so this keeps renewing
  // indefinitely without ever needing the token to actually expire first.
  useEffect(() => {
    if (!stored) return;
    const delayMs = Math.max(
      0,
      (stored.exp - REFRESH_LEAD_SECONDS - Date.now() / 1000) * 1000,
    );
    const timer = setTimeout(() => {
      void refreshToken().then((fresh) => {
        if (fresh) {
          storeToken(fresh);
          setStored(fresh);
        } else {
          // Refresh itself failed — most likely the app.pure.dev session
          // is genuinely gone (signed out / cookie cleared elsewhere), not
          // a transient blip (a network hiccup would still resolve fresh
          // === null here too, but there's no lower-risk way to tell them
          // apart from a single failed attempt; the full redirect flow
          // re-establishes a session if one exists, so this is safe
          // either way — worst case it's a needed re-login).
          setRedirecting(true);
          redirectToSso();
        }
      });
    }, delayMs);
    return () => clearTimeout(timer);
  }, [stored]);

  if (!stored) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8 text-sm text-muted-foreground">
        {redirecting ? "Signing in via app.pure.dev…" : "Loading…"}
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ token: stored.token }}>
      {children}
    </AuthContext.Provider>
  );
}
